/* Nanref motion (v6, Oct 17 2026; v7 adds info pages; v9 adds About and search): scroll reveals.
   Progressive enhancement: nothing is hidden unless this script runs, and anything
   already on screen when the page loads is never hidden (keeps first paint instant).
   Each block animates once, then its helper classes are removed. */
(() => {
  if (!('IntersectionObserver' in window)) return;
  if (window.Shopify && window.Shopify.designMode) return;

  const TARGETS = [
    // Homepage
    '.nf-trust__item',
    '.nf-categories__heading',
    '.nf-category',
    '.section-resource-list__header',
    '.resource-list--grid > .resource-list__item',
    '.resource-list__carousel',
    '.nf-feature__inner > *',
    '.nf-spot__copy',
    '.nf-spot__hero',
    '.nf-spot__card',
    '.nf-values > .nf-eyebrow',
    '.nf-values > h2',
    '.nf-values__grid > *',
    // Collection and search grids
    '.product-grid__item',
    '.nf-rel__tile',
    // Product page
    '.nx-features > .nx-kicker',
    '.nx-features > h2',
    '.nx-feature-grid > *',
    '.nx-full-description',
    '.nx-detail-grid > *',
    '.nx-reviews',
    '.nx-related-heading > *',
    // Info, contact and policy pages (v7)
    '.nf-info__facts > li',
    '.nf-info__panel',
    '.nf-faq__group',
    '.nf-contact__card',
    '.nf-helpcta',
    // About page and search page (v9)
    '.nf-about__portrait',
    '.nf-about__note',
    '.nf-about__head',
    '.nf-about__brand',
    '.nf-about__step',
    '.nf-about__promise',
    '.nf-about__cta',
    '.nf-search__cats > li',
  ].join(',');

  const STAGGER_MS = 70;
  const MAX_STEPS = 6;

  const found = Array.from(document.querySelectorAll(TARGETS));
  if (!found.length) return;
  const set = new Set(found);
  const viewH = window.innerHeight;

  const io = new IntersectionObserver(reveal, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  for (const el of found) {
    // Only the outermost target animates; nested targets ride along with their parent.
    let p = el.parentElement;
    let nested = false;
    while (p) {
      if (set.has(p)) { nested = true; break; }
      p = p.parentElement;
    }
    if (nested) continue;

    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue; // hidden (display: none)
    if (r.top < viewH * 0.92 && r.bottom > 0) continue; // already on screen: leave it alone

    el.classList.add('nf-rv');
    io.observe(el);
  }

  function reveal(entries) {
    const hits = entries
      .filter((e) => e.isIntersecting)
      .map((e) => e.target)
      .sort((a, b) => {
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        return Math.round(ra.top - rb.top) || ra.left - rb.left;
      });

    hits.forEach((el, i) => {
      io.unobserve(el);
      el.style.setProperty('--nf-rv-delay', `${Math.min(i, MAX_STEPS) * STAGGER_MS}ms`);
      el.classList.add('nf-rv--in');

      let cleaned = false;
      const cleanup = (ev) => {
        if (cleaned || (ev && ev.target !== el)) return;
        cleaned = true;
        el.removeEventListener('transitionend', cleanup);
        el.classList.remove('nf-rv', 'nf-rv--in');
        el.style.removeProperty('--nf-rv-delay');
      };
      el.addEventListener('transitionend', cleanup);
      window.setTimeout(cleanup, 1600);
    });
  }
})();

/* "On this page" links: highlight the section being read (v7).
   Uses the same headings the links point to; no scroll listeners. */
(() => {
  const links = Array.from(document.querySelectorAll('.nf-toc a[href^="#"]'));
  if (!links.length || !('IntersectionObserver' in window)) return;

  const byId = new Map();
  for (const a of links) {
    const id = decodeURIComponent(a.getAttribute('href').slice(1));
    const target = id && document.getElementById(id);
    if (target) byId.set(target, a);
  }
  if (!byId.size) return;

  const visible = new Set();
  let current = null;

  function setActive(a) {
    if (a === current) return;
    if (current) current.classList.remove('is-active');
    current = a;
    if (!a) return;
    a.classList.add('is-active');
    // On phones the list is a sideways strip: keep the active chip in view.
    const strip = a.closest('ol');
    if (strip && strip.scrollWidth > strip.clientWidth + 4) {
      const left = a.offsetLeft - (strip.clientWidth - a.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
    }
  }

  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) visible.add(e.target);
      else visible.delete(e.target);
    }
    let best = null;
    for (const t of visible) {
      if (!best || t.getBoundingClientRect().top < best.getBoundingClientRect().top) best = t;
    }
    if (best) setActive(byId.get(best));
  }, { rootMargin: '-20% 0px -55% 0px', threshold: 0 });

  byId.forEach((_, t) => io.observe(t));

  for (const a of links) {
    a.addEventListener('click', () => setActive(a));
  }
})();
